import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import { Card, CardContent, Typography, Box, Grid, Chip, Tooltip } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js'
import InfoIcon from '@mui/icons-material/Info'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend)

/**
 * Convert kW/Ton to COP (Coefficient of Performance)
 *
 * Formula derivation (per ASHRAE Handbook - Fundamentals):
 * - 1 ton of refrigeration = 12,000 BTU/hr
 * - 1 kW = 3,412 BTU/hr
 * - COP = (Cooling Output in BTU/hr) / (Power Input in BTU/hr)
 * - COP = (12,000 BTU/hr/ton) / (kW/ton × 3,412 BTU/hr/kW)
 * - COP = 12,000 / (kW/ton × 3,412) = 3.517 / kW/ton
 *
 * Reference: ASHRAE Standard 90.1, AHRI Standard 550/590
 */
function kwTonToCOP(kwTon) {
  return kwTon > 0 ? 3.517 / kwTon : 0
}

/**
 * Convert kW/Ton to EER (Energy Efficiency Ratio)
 *
 * Formula derivation:
 * - EER = (Cooling Output in BTU/hr) / (Power Input in Watts)
 * - EER = 12,000 BTU/hr/ton / (kW/ton × 1000 W/kW)
 * - EER = 12,000 / (kW/ton × 1000) = 12 / kW/ton
 *
 * Note: EER is measured at specific conditions (95°F outdoor, 80°F indoor, 50% RH)
 * Reference: AHRI Standard 210/240
 */
function kwTonToEER(kwTon) {
  return kwTon > 0 ? 12 / kwTon : 0
}

function EngineeringMetrics({ data, title = 'HVAC Engineering Metrics' }) {
  const metrics = useMemo(() => {
    const withKwTon = data.filter(d => d.kw_per_ton_before && d.kw_per_ton_after)

    if (withKwTon.length === 0) {
      return null
    }

    // Group by equipment type
    const byType = {}
    withKwTon.forEach(study => {
      const type = study.equipment_type || 'Unknown'
      if (!byType[type]) {
        byType[type] = {
          kwTonBefore: [],
          kwTonAfter: [],
          count: 0
        }
      }
      byType[type].kwTonBefore.push(study.kw_per_ton_before)
      byType[type].kwTonAfter.push(study.kw_per_ton_after)
      byType[type].count++
    })

    // Calculate averages and derived metrics
    const types = Object.keys(byType).sort()
    const metricsData = types.map(type => {
      const avgKwBefore = byType[type].kwTonBefore.reduce((a, b) => a + b, 0) / byType[type].count
      const avgKwAfter = byType[type].kwTonAfter.reduce((a, b) => a + b, 0) / byType[type].count

      return {
        type,
        count: byType[type].count,
        kwTonBefore: avgKwBefore,
        kwTonAfter: avgKwAfter,
        copBefore: kwTonToCOP(avgKwBefore),
        copAfter: kwTonToCOP(avgKwAfter),
        eerBefore: kwTonToEER(avgKwBefore),
        eerAfter: kwTonToEER(avgKwAfter),
        copImprovement: ((kwTonToCOP(avgKwAfter) - kwTonToCOP(avgKwBefore)) / kwTonToCOP(avgKwBefore) * 100),
        eerImprovement: ((kwTonToEER(avgKwAfter) - kwTonToEER(avgKwBefore)) / kwTonToEER(avgKwBefore) * 100)
      }
    })

    // Overall averages
    const overallKwBefore = withKwTon.reduce((sum, d) => sum + d.kw_per_ton_before, 0) / withKwTon.length
    const overallKwAfter = withKwTon.reduce((sum, d) => sum + d.kw_per_ton_after, 0) / withKwTon.length

    return {
      byType: metricsData,
      overall: {
        n: withKwTon.length,
        kwTonBefore: overallKwBefore,
        kwTonAfter: overallKwAfter,
        copBefore: kwTonToCOP(overallKwBefore),
        copAfter: kwTonToCOP(overallKwAfter),
        eerBefore: kwTonToEER(overallKwBefore),
        eerAfter: kwTonToEER(overallKwAfter),
        copImprovement: ((kwTonToCOP(overallKwAfter) - kwTonToCOP(overallKwBefore)) / kwTonToCOP(overallKwBefore) * 100),
        eerImprovement: ((kwTonToEER(overallKwAfter) - kwTonToEER(overallKwBefore)) / kwTonToEER(overallKwBefore) * 100)
      }
    }
  }, [data])

  if (!metrics) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Insufficient kW/Ton data for engineering metrics calculation.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const chartData = {
    labels: metrics.byType.map(m => m.type),
    datasets: [
      {
        label: 'COP Before',
        data: metrics.byType.map(m => m.copBefore),
        backgroundColor: 'rgba(239, 83, 80, 0.7)',
        borderColor: 'rgba(239, 83, 80, 1)',
        borderWidth: 1
      },
      {
        label: 'COP After',
        data: metrics.byType.map(m => m.copAfter),
        backgroundColor: 'rgba(160, 206, 78, 0.7)',
        borderColor: 'rgba(160, 206, 78, 1)',
        borderWidth: 1
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const idx = context.dataIndex
            const m = metrics.byType[idx]
            if (context.datasetIndex === 1) {
              return `Improvement: +${m.copImprovement.toFixed(1)}%`
            }
            return ''
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'COP (Coefficient of Performance)'
        }
      }
    }
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Chip label={`n=${metrics.overall.n}`} size="small" />
          <Tooltip title="COP = Coefficient of Performance (3.517/kW/Ton). EER = Energy Efficiency Ratio (12/kW/Ton). Higher values = better efficiency.">
            <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>

        {/* Overall Metrics Summary */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">COP Before</Typography>
              <Typography variant="h6" color="error.main">{metrics.overall.copBefore.toFixed(2)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">COP After</Typography>
              <Typography variant="h6" color="success.main">{metrics.overall.copAfter.toFixed(2)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">EER Before</Typography>
              <Typography variant="h6" color="error.main">{metrics.overall.eerBefore.toFixed(1)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">EER After</Typography>
              <Typography variant="h6" color="success.main">{metrics.overall.eerAfter.toFixed(1)}</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* COP Improvement Highlight */}
        <Box sx={{ mb: 2, p: 1, bgcolor: 'rgba(160, 206, 78, 0.15)', border: '1px solid', borderColor: 'primary.main', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="primary.main">
            Overall COP Improvement: <strong>+{metrics.overall.copImprovement.toFixed(1)}%</strong> |
            EER Improvement: <strong>+{metrics.overall.eerImprovement.toFixed(1)}%</strong>
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 200 }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default EngineeringMetrics
